import { create, type StoreApi, type UseBoundStore } from "zustand";

export interface Balances {
  credits: number;
  RAT: number;
  POL: number;
  ALY: number;
  ENC: number;
  SCR: number;
  stamina: number;
  vipPoints: number;
  creditAccruedAt: number;
  staminaAccruedAt: number;
}

export interface BalancesState {
  balances: Balances | null;
  reset(): void;
  setBalances(b: Balances, collectionRef?: string): void;
  /** Applies a WS push: the server's authoritative balance after the change. */
  applyCreditChange(balance: number, reason: string): void;
  applyResourceDeltas(deltas: Partial<Record<keyof Balances, number>>, collectionRef?: string): void;
  /** Credits including minutes elapsed since the last server accrual. */
  liveCredits(nowMs: number): number;
  msToNextCredit(nowMs: number): number;
}

const MINUTE = 60_000;

export function createBalancesStore(): UseBoundStore<StoreApi<BalancesState>> {
  const collections = new Set<string>();
  const remember = (ref?: string) => {
    if (!ref) return;
    collections.add(ref);
    if (collections.size > 500) collections.delete(collections.values().next().value!);
  };
  return create<BalancesState>((set, get) => ({
    balances: null,
    reset: () => { collections.clear(); set({ balances: null }); },
    setBalances: (balances, ref) => { remember(ref); set({ balances }); },
    applyCreditChange: (balance, reason) => {
      const b = get().balances;
      if (!b) return;
      // An accrual push carries the balance after adding whole minutes; move the accrual point forward accordingly.
      if (reason === "accrual") {
        const gained = balance - b.credits;
        set({ balances: { ...b, credits: balance, creditAccruedAt: b.creditAccruedAt + Math.max(0, gained) * MINUTE } });
      } else set({ balances: { ...b, credits: balance } });
    },
    applyResourceDeltas: (deltas, ref) => {
      if (ref && collections.has(ref)) return;
      const b = get().balances;
      if (!b) return;
      remember(ref);
      const next = { ...b };
      const rec = next as unknown as Record<string, number>;
      for (const [k, v] of Object.entries(deltas)) if (typeof v === "number" && k in next) rec[k] = (rec[k] ?? 0) + v;
      set({ balances: next });
    },
    liveCredits: (nowMs) => {
      const b = get().balances;
      if (!b) return 0;
      return b.credits + Math.max(0, Math.floor((nowMs - b.creditAccruedAt) / MINUTE));
    },
    msToNextCredit: (nowMs) => {
      const b = get().balances;
      if (!b) return MINUTE;
      const elapsed = Math.max(0, nowMs - b.creditAccruedAt) % MINUTE;
      return MINUTE - elapsed;
    },
  }));
}

export const useBalances = createBalancesStore();
